import { useState, useEffect, useContext, useCallback } from "react";
import apiClient from "../../services/apiClient";
import { Table, Typography, message, Spin, Divider } from "antd";
import { ThemeContext } from "../../context/ThemeContext";
import "./AdminDashboard.css";

const { Title } = Typography;

const IssuerEsgRatingsManagement = () => {
  const { isDark } = useContext(ThemeContext);
  const baseUrl = process.env.REACT_APP_BASE_URL || "";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchEsgRatings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`${baseUrl}/master-issuer/esg-ratings/all`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      message.error("Failed to load ESG ratings");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  useEffect(() => {
    fetchEsgRatings();
  }, [fetchEsgRatings]);

  const rowKey = (row) =>
    `${row.companyName || ""}|${row.sector || ""}|${row.dateOfRating || ""}`;

  /** Ant Design defaults to nowrap + ellipsis; allow multi-line wrap inside cells. */
  const tableStyles = {
    body: {
      cell: {
        whiteSpace: "normal",
        wordBreak: "break-word",
        verticalAlign: "top",
      },
    },
    header: {
      cell: {
        whiteSpace: "normal",
        wordBreak: "break-word",
        verticalAlign: "bottom",
      },
    },
  };

  const columns = [
    {
      title: "Company name",
      dataIndex: "companyName",
      key: "companyName",
      width: "24%",
      render: (t) => <span style={{ fontWeight: 600 }}>{t || "—"}</span>,
    },
    {
      title: "Sector",
      dataIndex: "sector",
      key: "sector",
      width: "18%",
      render: (t) => t || "—",
    },
    {
      title: "ESG Score",
      dataIndex: "esgRating",
      key: "esgRating",
      width: "8%",
      align: "right",
      render: (v) => (v != null && v !== "" ? v : "—"),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: "10%",
      render: (t) => t || "—",
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      width: "18%",
      render: (t) => t || "—",
    },
    {
      title: "Date of rating",
      dataIndex: "dateOfRating",
      key: "dateOfRating",
      width: "12%",
      render: (t) => t || "—",
    },
  ];

  return (
    <div className={`admin-dashboard-container ${isDark ? "dark" : ""}`}>
      <div
        className="admin-users-hero"
        style={{
          background: isDark
            ? "linear-gradient(135deg, rgba(255, 255, 240, 0.15) 0%, rgba(230, 230, 255, 0.2) 50%, rgba(255, 230, 240, 0.15) 100%)"
            : "linear-gradient(135deg, rgba(255, 255, 240, 0.4) 0%, rgba(230, 230, 255, 0.5) 50%, rgba(255, 230, 240, 0.4) 100%)",
          borderRadius: 16,
          padding: "48px 48px 40px 48px",
          marginBottom: 32,
          boxShadow: isDark ? "0 4px 20px rgba(0, 0, 0, 0.3)" : "0 4px 20px rgba(0, 0, 0, 0.08)",
          border: isDark ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.08)",
        }}
      >
        <Title
          level={1}
          style={{
            margin: 0,
            marginBottom: 12,
            fontWeight: 800,
            fontSize: 42,
            color: isDark ? "#ffffff" : "#1a1a1a",
          }}
        >
          ESG Rating List
        </Title>
      </div>

      <Divider
        style={{
          margin: 0,
          marginBottom: 24,
          borderColor: isDark ? "#333c4a" : undefined,
        }}
      />

      <Spin spinning={loading}>
        <Table
          className="issuer-esg-ratings-table"
          rowKey={rowKey}
          dataSource={rows}
          columns={columns}
          style={{ width: "100%" }}
          styles={tableStyles}
          pagination={{
            pageSize: 20,
            showSizeChanger: false,
            showTotal: (total) => `Total ${total} records`,
          }}
        />
      </Spin>
    </div>
  );
};

export default IssuerEsgRatingsManagement;
